/**
 * EditLockBanner — full-width amber banner shown when another user holds the
 * edit lock on a resource. Surfaces the holder's name, when they started, a
 * manual refresh button (the parent hook also auto-polls every 10s), and a
 * "Take over editing" button gated by a native confirm() dialog. Anyone can
 * override — the team is small and co-located, so coordination happens
 * verbally / via Slack rather than via permission gating.
 */

import React from 'react';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { LockHolder } from '../utils/useEditLock';
import { Button } from './ui/Button';
import { IconWarning } from './ui/icons';

dayjs.extend(relativeTime);

interface Props {
  holder: LockHolder | null;
  onRefresh?: () => void;
  /** Force-takeover handler. When provided, renders the "Take over editing" button. */
  onTakeover?: () => void;
  /** Heading text — defaults to "This event is being edited". */
  resourceLabel?: string;
}

const EditLockBanner: React.FC<Props> = ({ holder, onRefresh, onTakeover, resourceLabel = 'This event' }) => {
  const name = holder?.user_name || 'Someone';
  const since = holder?.acquired_at ? dayjs(holder.acquired_at).fromNow() : 'just now';

  const handleTakeover = () => {
    if (!onTakeover) return;
    // eslint-disable-next-line no-alert
    const confirmed = window.confirm(
      `Take over editing from ${name}?\n\n` +
      `Check with them first, in person or on Slack. Their page reloads, and any unsaved typing is offered to them to copy before it does.`,
    );
    if (confirmed) onTakeover();
  };

  return (
    <div className="my-4 flex items-start gap-3 rounded-panel bg-amber-500/10 px-4 py-3">
      <IconWarning className="mt-0.5 h-5 w-5 shrink-0 text-amber-400" />
      <div className="min-w-0 flex-1">
        <div className="text-sm font-semibold text-on-surface">
          {resourceLabel} is being edited by {name}
        </div>
        <div className="mt-0.5 text-xs text-on-surface-variant">
          Started {since}. This form is read-only while they edit. It unlocks after 10 idle minutes, or check with them, then choose Take over editing.
        </div>
      </div>
      <div className="flex shrink-0 flex-col items-end gap-1">
        {onTakeover && (
          <Button size="sm" onClick={handleTakeover}>
            Take over editing
          </Button>
        )}
        {onRefresh && (
          <Button size="sm" variant="quiet" onClick={onRefresh}>
            Check again
          </Button>
        )}
      </div>
    </div>
  );
};

export default EditLockBanner;
